import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Fine, MONO, Page, Result, SORA, Text, Title } from '../components/AgentLoginLayout';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { ApiV2Error, claimApps, ClaimPreview, previewClaim } from '../utils/apiV2';

/**
 * The claim screen: where the link an AI agent shows ends up
 * (`/claim#b4a_claim_<token>`, by way of the API v2's /claim).
 *
 * The agent deployed on a temporary account, which lasts 24 hours. The person
 * sees what it made — name, address, whether it is running — and one button
 * moves it to their account, where it stays. They sign in or sign up first,
 * the way everyone does.
 *
 * The token is what moves apps into an account, so it is treated like a
 * password: read from the address once and taken out of it, kept for this tab
 * only, and sent in the body of the two calls. It never rides in an address
 * that a server would log — not even the login's return address.
 */

const TOKEN_KEY = 'b4a.claimToken';
const TOKEN = /^#?(b4a_claim_[A-Za-z0-9_-]{43})$/;

type Step =
  | { name: 'loading' }
  | { name: 'ready'; preview: ClaimPreview }
  | { name: 'moved'; appName: string }
  | { name: 'noSlot'; freeApps: { id: string; name: string }[] }
  | { name: 'unverified' }
  | { name: 'result'; title: string; text: string };

/** The token of this visit: from the address the first time, from the tab after. */
const takeToken = (): string | null => {
  const fromAddress = TOKEN.exec(window.location.hash);
  if (fromAddress) {
    try {
      window.sessionStorage.setItem(TOKEN_KEY, fromAddress[1]);
    } catch (e) {
      // No storage (a private window that forbids it): the visit still works,
      // only signing in would lose the link.
    }
    // Out of the address bar and of the history.
    window.history.replaceState(null, '', window.location.pathname);
    return fromAddress[1];
  }
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

const forgetToken = () => {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // Nothing was kept.
  }
};

const LIVE = ['READY', 'SLEEPY', 'SLEEPING', 'WAKING_UP', 'RENEWING'];
const IN_PROGRESS = ['INITIALIZING', 'INITIALIZED', 'QUEUED', 'DEPLOYING'];

const statusOf = (status: string | null) => {
  if (!status) {
    return 'Not deployed yet';
  }
  if (LIVE.includes(status)) {
    return 'Online';
  }
  if (IN_PROGRESS.includes(status)) {
    return 'Deploying';
  }
  return status === 'FAILED' ? 'Last deploy failed' : 'Offline';
};

const timeLeft = (expiresAt: string) => {
  const hours = (new Date(expiresAt).getTime() - Date.now()) / (60 * 60 * 1000);
  if (hours < 1) {
    return 'in less than 1 hour';
  }
  const whole = Math.floor(hours);
  return `in ${whole} ${whole === 1 ? 'hour' : 'hours'}`;
};

const resultOf = (e: unknown): Step => {
  if (e instanceof ApiV2Error && e.code === 'FREE_SLOT_UNAVAILABLE') {
    const freeApps = e.details?.freeApps;
    return { name: 'noSlot', freeApps: Array.isArray(freeApps) ? freeApps : [] };
  }
  if (e instanceof ApiV2Error && e.code === 'VERIFICATION_PENDING') {
    return { name: 'unverified' };
  }
  if (e instanceof ApiV2Error && e.status === 410) {
    return {
      name: 'result',
      title: 'This temporary account expired',
      text: 'What it created was deleted. Ask your AI agent to deploy again, and open the new link within 24 hours.',
    };
  }
  if (e instanceof ApiV2Error && e.status === 409) {
    return {
      name: 'result',
      title: 'This link was already used',
      text: e.message,
    };
  }
  if (e instanceof ApiV2Error && e.status === 404) {
    return {
      name: 'result',
      title: 'This link is not valid',
      text: 'Ask your AI agent for the claim link again.',
    };
  }
  return {
    name: 'result',
    title: 'Something went wrong',
    text: 'Reload this page. Your app is still there until the temporary account expires.',
  };
};

const button =
  'flex h-[52px] items-center justify-center rounded-lg bg-[#3F9A59] text-base font-semibold text-white hover:bg-[#368A4E] disabled:opacity-60';

const ClaimApps = () => {
  const navigate = useNavigate();
  const [token] = useState(takeToken);
  const [step, setStep] = useState<Step>({ name: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setStep(resultOf(new ApiV2Error(404, 'CLAIM_NOT_FOUND', '')));
      return;
    }
    try {
      setStep({ name: 'ready', preview: await previewClaim(token) });
    } catch (e) {
      // Not signed in: the site's login brings the person back to this page.
      // The token stays in this tab, out of the return address.
      if (e instanceof ApiV2Error && e.status === 401) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
            `${window.location.origin}${window.location.pathname}`
          )}`
        );
        return;
      }
      setStep(resultOf(e));
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const move = async () => {
    if (!token) {
      return;
    }
    // The apps the screen is showing: what moved, when the API finished the
    // move in a request it no longer remembers.
    const shown = step.name === 'ready' ? step.preview.apps : [];
    setIsSubmitting(true);
    try {
      const { apps } = await claimApps(token);
      const moved = apps[0] || shown[0];
      forgetToken();
      setStep({ name: 'moved', appName: moved?.name || 'Your app' });
      navigate(moved ? `/apps/${moved.id}` : '/apps', { replace: true });
    } catch (e) {
      setStep(resultOf(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const retry = async () => {
    setIsSubmitting(true);
    setStep({ name: 'loading' });
    await load();
    setIsSubmitting(false);
  };

  if (step.name === 'loading') {
    return <Page>{null}</Page>;
  }

  if (step.name === 'result') {
    return <Result title={step.title} text={step.text} />;
  }

  if (step.name === 'moved') {
    return <Result title={`${step.appName} is in your account`} text="Taking you to it." />;
  }

  if (step.name === 'unverified') {
    return (
      <Page low>
        <Card>
          <div className="flex flex-col items-center gap-2.5">
            <Title>Confirm your email</Title>
            <Text>Open the link we sent to your email, then come back to this page.</Text>
          </div>
          <button type="button" className={button} style={{ fontFamily: SORA }} disabled={isSubmitting} onClick={retry}>
            I confirmed it
          </button>
        </Card>
      </Page>
    );
  }

  if (step.name === 'noSlot') {
    return (
      <Page low>
        <Card>
          <div className="flex flex-col items-center gap-2.5">
            <Title>Your account has no free app slot</Title>
            <Text>Delete one of your free apps, or move one to a paid plan, then come back to this page.</Text>
          </div>
          {step.freeApps.length > 0 && (
            <div className="flex flex-col gap-2">
              {step.freeApps.map((app) => (
                <a
                  key={app.id}
                  className="text-center text-[15px] text-[#5DBB72] hover:text-[#7ED08F] hover:underline"
                  href={`/apps/${app.id}/settings`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {app.name}
                </a>
              ))}
            </div>
          )}
          <button type="button" className={button} style={{ fontFamily: SORA }} disabled={isSubmitting} onClick={retry}>
            Try again
          </button>
          <Fine>
            <span>The temporary account keeps its deadline meanwhile.</span>
          </Fine>
        </Card>
      </Page>
    );
  }

  const { preview } = step;
  const many = preview.apps.length !== 1;

  if (preview.apps.length === 0) {
    return (
      <Result
        title="Nothing to move yet"
        text="This temporary account has no app. Ask your AI agent to deploy, then open the link again."
      />
    );
  }

  return (
    <Page>
      <Card>
        <div className="flex flex-col items-center gap-2.5">
          <Title large>{many ? 'Keep these apps' : 'Keep this app'}</Title>
          <Text>
            {preview.createdBy || 'An AI agent'} deployed {many ? 'them' : 'it'} on a temporary account. Move{' '}
            {many ? 'them' : 'it'} to your Back4App account to keep {many ? 'them' : 'it'} online.
          </Text>
        </div>
        <div className="flex flex-col gap-4">
          {preview.apps.map((app) => (
            <div key={app.id} className="flex flex-col items-center gap-1 text-center">
              <span className="text-base font-medium text-white">{app.name}</span>
              {app.url && (
                <a
                  className="break-all text-[13px] text-[#AEB8C8] hover:text-white hover:underline"
                  style={{ fontFamily: MONO }}
                  href={app.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {app.url.replace(/^https?:\/\//, '')}
                </a>
              )}
              <span className="text-[13px] text-[#7F8BA0]">{statusOf(app.lastDeploymentStatus)}</span>
            </div>
          ))}
        </div>
        <button type="button" className={button} style={{ fontFamily: SORA }} disabled={isSubmitting} onClick={move}>
          {isSubmitting ? 'Moving…' : 'Move to my account'}
        </button>
        <Fine>
          <span className="text-[#F3C969]">Will be permanently deleted {timeLeft(preview.expiresAt)} if not moved.</span>
          <span>Signed in as {preview.signedInAs}</span>
        </Fine>
      </Card>
    </Page>
  );
};

export default ClaimApps;
